/**
 * CommentSection — 게시글 댓글 영역 (채팅 쓰레드식 답글)
 *
 * - 채팅과 동일한 패턴: 답글 버튼 클릭 → 하단 단일 입력창에 부모 메시지 인용 배너 표시
 * - parent_comment_id = 쓰레드 루트 / reply_to_comment_id = 같은 쓰레드 내 인용 대상
 * - QnA 게시판: 채택 기능
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Comment as CommentModel, CommentAttachment, PostAuthor, Post } from '../../models/Board';
import {
  createComment, updateComment, deleteComment, acceptAnswer, unresolveQuestion, fetchReplies,
  toggleCommentReaction, recordCommentRead, getCommentReadReceiptsBatch,
  uploadCommentAttachment, notifyNewComment,
} from '../../services/BoardService';
import { askAI } from '../../services/AiMemoAnalyzer';
import { ChatFilePickerModal } from './ChatFilePickerModal';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  Send, Trash2, CheckCircle2, Award, ChevronDown, ChevronRight,
  CornerDownRight, X, Plus, Paperclip, MessageSquareQuote, FileText, Loader2,
  Languages, Pencil, Check, Hourglass,
} from 'lucide-react';
import { IconMoreVertical } from '../components/FigmaIcons';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { IconSmilePlus } from '../components/FigmaIcons';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from '../../lib/relativeTime';
import { toast } from '../../components/ui/use-toast';
import { UserAvatar } from '../components/UserAvatar';
import MessageFileItem from '../components/MessageFileItem';
import { FileAttachment } from '../../models/Message';
import type { MentionUser } from './MentionSuggestion';
import MentionTextArea from '../components/MentionTextArea';
import type { SuggestionDataItem } from 'react-mentions';
import { UtilApp } from '../../services/Utils/UtilApp';

/** 댓글 마크업 형식 `[이름](user:id)` 에서 user ID 추출 */
function extractMentionIdsFromMarkup(text: string): string[] {
  const ids = new Set<string>();
  const regex = /@?\[(?:.+?)\]\(user:(.*?)\)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m[1]) ids.add(m[1]);
  }
  return Array.from(ids);
}


interface Props {
  postId: string;
  comments: CommentModel[];
  onCommentAdded: () => void;
  /** QnA 게시판 여부 */
  isQna?: boolean;
  /** 현재 게시글 (QnA 채택 상태 확인용) */
  post?: Post;
  /** 채택 후 게시글 업데이트 콜백 */
  onPostUpdated?: (post: Post) => void;
  /** 댓글 작성 권한 (기본 true) */
  canComment?: boolean;
  /** @멘션 대상 멤버 목록 */
  mentionMembers?: MentionUser[];
  /** 댓글 단위 확인 현황(읽음 여부) 표시 — Tasks에서만 true */
  showReadReceipts?: boolean;
  /** 채널/방 id — 있으면 "채팅 파일에서 가져오기" 기능 활성화 */
  channelId?: string;
  /** @전체(전원 멘션) 권한 — 있으면 멘션 후보에 @전체 노출 */
  canMentionAll?: boolean;
}

/** HTML 이스케이프 (XSS 방지) */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** 정규식 메타문자 이스케이프 */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 이미 escape/sanitize 된 HTML 문자열에 경량 마크다운을 적용.
 * 사용자 입력은 이 함수 호출 전에 escape/sanitize 되므로 고정 태그만 삽입 → XSS 안전.
 * - `**굵게**` → <strong>  (getTextAsHTML 경로는 이미 처리되어 여기선 no-op)
 * - `*기울임*` → <em>       (볼드 변환 후라 남은 단일 * 만 매칭)
 * - 한 줄에 `---`(3+) → <hr>
 */
function formatMarkdown(html: string): string {
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
  html = html.replace(/(^|\n)[ \t]*-{3,}[ \t]*(?=\n|$)/g, '$1<hr class="my-2 border-t border-border" />');
  return html;
}

/**
 * 댓글 텍스트를 렌더용 HTML 로 변환 — `@이름` 멘션 + 경량 마크다운(굵게/기울임/구분선).
 * - 신규 형식 `[이름](user:id)` → UtilApp.getTextAsHTML (escape + 멘션 + linkify + sanitize)
 * - 구 형식 plain `@이름` → mentioned_user_ids + members 매칭 fallback
 * 두 경로 모두 최종적으로 formatMarkdown 을 거쳐 마크다운이 렌더된다.
 */
function renderCommentText(
  text: string,
  mentionedIds: string[] | null | undefined,
  members: MentionUser[] | undefined,
  currentUserId?: string,
): string {
  if (!text) return '';
  const hasMarkup = /@?\[(?:.+?)\]\(user:.*?\)/.test(text);
  if (hasMarkup) {
    return formatMarkdown(UtilApp.getTextAsHTML(text, currentUserId ?? ''));
  }
  // 구 형식: mentioned_user_ids + 이름 매칭으로 fallback
  let result = escapeHtml(text);
  if (mentionedIds && mentionedIds.length > 0 && members && members.length > 0) {
    const mentionedSet = new Set(mentionedIds);
    const candidates = members
      .filter(m => mentionedSet.has(m.id))
      .sort((a, b) => b.name.length - a.name.length);
    for (const m of candidates) {
      const isMe = m.id === currentUserId;
      const escName = escapeRegex(escapeHtml(m.name));
      const regex = new RegExp(`@${escName}(?=\\s|$|[.,!?])`, 'g');
      result = result.replace(regex, `<span class="mention ${isMe ? 'is-me' : 'is-other'}">@${escapeHtml(m.name)}</span>`);
    }
  }
  return formatMarkdown(result);
}


// ChatFilePickerModal 은 별도 파일로 추출 — PostCreateModal / PostDetailOverlay 에서도 재사용
// (./ChatFilePickerModal.tsx import 로 사용)

// ─── CommentItem (댓글 1건 + 대댓글 토글/목록/입력) ──────────

interface CommentItemProps {
  comment: CommentModel;
  isQna?: boolean;
  isPostAuthor: boolean;
  acceptedCommentId?: string;
  acceptingId: string | null;
  onAccept: (commentId: string) => void;
  onUnresolve: () => void;
  onDelete: (commentId: string) => void;
  /** 답글(쓰레드 자식)이면 true — 채팅 쓰레드처럼 평탄(깊이 1) 유지 */
  isReply?: boolean;
  /** @멘션 대상 멤버 목록 (확인 현황의 이름/아바타 매핑용) */
  mentionMembers?: MentionUser[];
  /** 댓글 단위 확인 현황 표시 여부 (Tasks 전용) */
  showReadReceipts?: boolean;
  /** commentId → 열람 기록 (showReadReceipts일 때만 의미 있음) */
  receiptsByCommentId?: Record<string, { user_id: string; read_at: string }[]>;
  /** 같은 쓰레드의 형제 답글 목록 (인용 헤더의 대상자 이름 조회용) — isReply일 때만 의미 있음 */
  threadSiblings?: CommentModel[];
  /** 답글 버튼 클릭 시 호출 — 메인 입력창의 답글 모드를 활성화 (CommentSection에서 처리) */
  onReplyClick: (comment: CommentModel) => void;
  /** 이 쓰레드(=이 댓글 id)에 새 답글이 추가됐을 때 부모 CommentSection이 증가시키는 신호. 변하면 replies 재로드 + 펼침. */
  refreshRepliesTrigger?: number;
}

const REACTION_TYPES = ['smile', 'thumbs-up', 'heart', 'check', 'shake-hand'] as const;

const CommentItem: React.FC<CommentItemProps> = ({
  comment, isQna, isPostAuthor, acceptedCommentId,
  acceptingId, onAccept, onUnresolve, onDelete, isReply, mentionMembers,
  showReadReceipts, receiptsByCommentId, threadSiblings, onReplyClick, refreshRepliesTrigger,
}) => {
  const { i18n } = useLingui();
  const isAccepted = isQna && acceptedCommentId === comment.id;
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState<CommentModel[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  // 수정 모드
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [savingEdit, setSavingEdit] = useState(false);
  const [localText, setLocalText] = useState(comment.text);
  // AI 번역
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const isMyComment = My.user?.id === comment.author.uid;
  // 리액션
  const [localReactions, setLocalReactions] = useState<Record<string, string[]>>(comment.reactions ?? {});
  const [isReacting, setIsReacting] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);

  const myReaction = useMemo(() => {
    const uid = My.user?.id;
    if (!uid) return null;
    for (const [type, uids] of Object.entries(localReactions)) {
      if (uids.includes(uid)) return type;
    }
    return null;
  }, [localReactions]);

  const handleCommentReaction = useCallback(async (reactionType: string) => {
    if (isReacting || !My.user?.id) return;
    setIsReacting(true);
    try {
      const updated = await toggleCommentReaction(comment.id, My.user.id, reactionType);
      setLocalReactions(updated.reactions ?? {});
    } catch (err) {
      console.error('[CommentItem] 리액션 실패:', err);
      toast({ title: t`Failed to add reaction`, variant: 'destructive' });
    } finally {
      setIsReacting(false);
    }
  }, [comment.id, isReacting]);

  const handleToggleReplies = useCallback(async () => {
    if (showReplies) {
      setShowReplies(false);
      return;
    }
    // 이미 미리 로드돼 있으면 펼치기만
    if (replies.length > 0) {
      setShowReplies(true);
      return;
    }
    setLoadingReplies(true);
    try {
      const data = await fetchReplies(comment.id);
      setReplies(data);
      setShowReplies(true);
    } catch (err) {
      console.error('[CommentItem] 답글 로드 실패:', err);
    } finally {
      setLoadingReplies(false);
    }
  }, [showReplies, comment.id, replies.length]);

  // 부모(CommentSection)가 이 쓰레드에 새 답글을 추가했을 때 → replies 재로드 + 자동 펼침
  useEffect(() => {
    if (!refreshRepliesTrigger || isReply) return;
    fetchReplies(comment.id).then(data => {
      setReplies(data);
      setShowReplies(true);
    }).catch(err => console.error('[CommentItem] refresh replies 실패:', err));
  }, [refreshRepliesTrigger, isReply, comment.id]);

  // 답글 미리 로드 — 답글자 아바타 표시를 위해 (펼치기 전에도 표시)
  useEffect(() => {
    if (isReply) return;
    if (comment.reply_count > 0 && replies.length === 0 && !loadingReplies) {
      fetchReplies(comment.id).then(setReplies).catch(err => {
        console.error('[CommentItem] preview replies 실패:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment.id, comment.reply_count, isReply]);

  // 답글자 고유 아바타 목록 (최대 3명)
  const replierAvatars = useMemo(() => {
    if (replies.length === 0) return [] as { uid: string; name: string; photo: string | null }[];
    const seen = new Set<string>();
    const unique: { uid: string; name: string; photo: string | null }[] = [];
    for (const r of replies) {
      if (!seen.has(r.author.uid)) {
        seen.add(r.author.uid);
        unique.push({ uid: r.author.uid, name: r.author.name, photo: r.author.photoUrl ?? null });
        if (unique.length >= 3) break;
      }
    }
    return unique;
  }, [replies]);

  // 외부에서 comment 객체가 바뀌면 (재페치 등) 로컬 텍스트도 동기화
  useEffect(() => {
    setLocalText(comment.text);
  }, [comment.text]);

  /** 수정 모드 진입 */
  const startEditing = () => {
    setEditText(localText);
    setIsEditing(true);
  };

  /** 수정 저장 */
  const handleSaveEdit = async () => {
    const next = editText.trim();
    if (!next || next === localText || savingEdit) {
      setIsEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await updateComment(comment.id, next);
      setLocalText(updated.text);
      setIsEditing(false);
    } catch (err) {
      console.error('[CommentItem] 수정 실패:', err);
      toast({ title: t`Failed to edit comment`, variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(localText);
    setIsEditing(false);
  };

  /** AI 번역 — 채팅의 translateMessage 패턴 미러링 */
  const handleTranslate = async () => {
    if (isTranslating) return;
    if (translatedText) {
      setTranslatedText(null);
      return;
    }
    if (!localText.trim()) return;
    setIsTranslating(true);
    try {
      const lang = navigator.language?.startsWith('ja') ? 'Japanese'
                 : navigator.language?.startsWith('en') ? 'English'
                 : 'Korean';
      const prompt = `Only Translate this text to ${lang}.\n\n${localText}`;
      const answer = await askAI(prompt, 0);
      if (answer?.trim()) {
        setTranslatedText(answer.trim());
      } else {
        toast({ title: t`Translation failed`, variant: 'destructive' });
      }
    } catch (err) {
      console.error('[CommentItem] 번역 실패:', err);
      toast({ title: t`Translation failed`, variant: 'destructive' });
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <div>
      <div
        className={cn(
          'group flex gap-2.5',
          isAccepted && 'bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 -mx-1',
          isReply && 'pl-2',
        )}
      >
        {/* 아바타 — prototype comfortable: 32px */}
        <UserAvatar
          src={comment.author.photoUrl}
          name={comment.author.name}
          className={cn('shrink-0 mt-0.5', isReply ? 'size-7' : 'size-8')}
        />

        <div className="flex-1 min-w-0">
          {/* 헤더: 작성자 정보 (좌) + 액션 버튼 (우측 끝) — prototype `.ih-comment-toolbar` 미러 */}
          <div className="flex items-start gap-1">
            <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
              <span className="text-body font-bold text-[color:rgb(var(--text-title))]">
                {comment.author.name}
              </span>
              {/* replyTo 인라인 표시 */}
              {isReply && comment.reply_to_comment_id && (() => {
                const target = threadSiblings?.find(s => s.id === comment.reply_to_comment_id);
                if (!target) return null;
                return (
                  <span className="inline-flex items-center text-xs text-muted-foreground">
                    <ChevronRight className="size-3 mx-0.5" />
                    <span className="font-semibold text-foreground">{target.author.name}</span>
                  </span>
                );
              })()}
              {isAccepted && (
                <span className="inline-flex items-center gap-0.5 text-meta-sm font-semibold text-badge-success ml-1">
                  <Award className="size-3" />
                  <Trans>Accepted answer</Trans>
                </span>
              )}
              <span className="text-meta text-muted-foreground ml-1">
                {formatRelativeTime(comment.created_at, i18n)}
              </span>
            </div>

            {/* 액션 버튼 영역 (우측 끝) — 항상 표시: 리액션 추가 / 답글 / 번역 / 더보기 */}
            <div className="flex items-center gap-0.5 shrink-0">
              {/* 리액션 추가 */}
              <Popover open={showReactionPicker} onOpenChange={setShowReactionPicker} modal>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      'inline-flex items-center justify-center size-[30px] rounded-full transition-colors cursor-pointer',
                      showReactionPicker ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                    title={t`Add reaction`}
                  >
                    <IconSmilePlus className="size-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-2 rounded-xl w-auto" side="bottom" align="end" sideOffset={4}>
                  <div className="flex items-center gap-0.5">
                    {REACTION_TYPES.map(type => {
                      const isSaved = myReaction === type;
                      return (
                        <button
                          key={type}
                          disabled={isReacting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCommentReaction(type);
                            setShowReactionPicker(false);
                          }}
                          className={cn(
                            'size-7 p-1 rounded-lg hover:bg-muted transition-colors cursor-pointer disabled:opacity-50',
                            isSaved && 'bg-primary/15 ring-1.5 ring-primary',
                          )}
                        >
                          <img src={`/assets/icon/ic-${type}-24.svg`} alt={type} className="w-full h-full" />
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              {/* 답글 — CornerDownRight (README §4-2) */}
              <button
                onClick={() => onReplyClick(comment)}
                className="inline-flex items-center justify-center size-[30px] rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                title={t`Reply`}
              >
                <CornerDownRight className="size-4" />
              </button>
              {/* AI 번역 — 텍스트 있을 때만 */}
              {localText.trim() && (
                <button
                  onClick={handleTranslate}
                  disabled={isTranslating}
                  className={cn(
                    'inline-flex items-center justify-center size-[30px] rounded-full transition-colors cursor-pointer disabled:opacity-50',
                    translatedText
                      ? 'text-blue-500 hover:bg-blue-500/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                  title={translatedText ? t`Close translation` : t`AI translation`}
                >
                  {isTranslating ? <Loader2 className="size-4 animate-spin" /> : <Languages className="size-4" />}
                </button>
              )}
              {/* More 메뉴 (수정/삭제) — 본인 댓글만, 채팅 MessagePopover와 동일 아이콘 */}
              {isMyComment && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="inline-flex items-center justify-center size-[30px] rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                      title={t`More options`}
                    >
                      <IconMoreVertical className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={4}>
                    <DropdownMenuItem onClick={startEditing} className="text-xs">
                      <Pencil className="mr-2 size-3.5" />
                      <Trans>Edit</Trans>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(comment.id)}
                      className="text-xs text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 size-3.5" />
                      <Trans>Delete</Trans>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="mt-1.5 space-y-2">
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={Math.min(8, Math.max(2, editText.split('\n').length))}
                autoFocus
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={handleCancelEdit}
                  disabled={savingEdit}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50"
                >
                  <Trans>Cancel</Trans>
                </button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit || !editText.trim() || editText.trim() === localText}
                  size="sm"
                  className="h-7 text-xs"
                >
                  {savingEdit ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {localText && (
                <p
                  className="text-body-lg text-[color:rgb(var(--text-body))] mt-1 whitespace-pre-wrap leading-[1.65]"
                  dangerouslySetInnerHTML={{
                    __html: renderCommentText(localText, comment.mentioned_user_ids, mentionMembers, My.user?.id),
                  }}
                />
              )}

              {/* AI 번역 결과 — 채팅의 translatedText 박스 패턴 */}
              {translatedText && (
                <div className="mt-2 p-2.5 bg-blue-500/10 rounded-lg border-l-4 border-blue-400">
                  <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                    <Languages className="inline size-3.5 mr-1 -mt-0.5 text-blue-400" />
                    {translatedText}
                  </div>
                </div>
              )}
              {isTranslating && (
                <div className="mt-2 p-2.5 bg-blue-500/10 rounded-lg border-l-4 border-blue-400 flex items-center gap-2">
                  <Loader2 className="size-3.5 animate-spin text-blue-400 shrink-0" />
                  <span className="text-sm text-muted-foreground"><Trans>Translating...</Trans></span>
                </div>
              )}
            </>
          )}

          {/* 첨부파일 렌더링 — 채팅과 동일한 표시(이미지 라이트박스/비디오 플레이어/문서 뷰어) */}
          {comment.attachments && comment.attachments.length > 0 && (() => {
            const files = comment.attachments.map(
              att => new FileAttachment(att.filename, att.url, att.sizeByte, att.group),
            );
            return (
              <div className="mt-2 flex flex-wrap gap-2">
                {files.map((file, idx) => (
                  <MessageFileItem
                    key={`${file.url}-${idx}`}
                    file={file}
                    files={files}
                    showTitle={false}
                  />
                ))}
              </div>
            );
          })()}

          {/* 액션 바: 답글 pill + 리액션 picker + 활성 리액션 + 채택/확인현황 — 채팅 RoomMessageItem 패턴 */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {/* 답글 pill (최상위 댓글, 답글 있을 때만) — 답글자 아바타 + 카운트 + 토글 */}
            {!isReply && comment.reply_count > 0 && (
              <button
                onClick={handleToggleReplies}
                disabled={loadingReplies}
                className="inline-flex items-center gap-1.5 h-7 px-2 bg-background border border-border rounded-md text-meta text-muted-foreground hover:bg-accent transition-colors shadow-sm cursor-pointer disabled:opacity-60"
              >
                {replierAvatars.length > 0 && (
                  <div className="flex -space-x-1">
                    {replierAvatars.map(a => (
                      <UserAvatar
                        key={a.uid}
                        src={a.photo}
                        name={a.name}
                        className="size-4 ring-1 ring-background"
                      />
                    ))}
                  </div>
                )}
                <span className="font-medium"><Trans>Replies {comment.reply_count}</Trans></span>
                {loadingReplies ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <ChevronDown className={cn('size-3 transition-transform', showReplies && 'rotate-180')} />
                )}
              </button>
            )}

            {/* 활성 리액션 뱃지 */}
            {REACTION_TYPES.map(type => {
              const uids = localReactions[type] ?? [];
              if (uids.length === 0) return null;
              const isActive = myReaction === type;
              return (
                <Popover key={type} modal>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        'inline-flex items-center gap-1.5 h-7 px-[11px] rounded-full text-label border transition-colors cursor-pointer',
                        isActive
                          ? 'bg-[var(--primary-alpha)] border-[color-mix(in_srgb,rgb(var(--primary))_45%,rgb(var(--border)))] text-primary font-semibold'
                          : 'bg-muted border-border hover:border-[color-mix(in_srgb,rgb(var(--foreground))_25%,rgb(var(--border)))]',
                      )}
                    >
                      <img src={`/assets/icon/ic-${type}-24.svg`} alt={type} className="size-4" />
                      <span className={cn('tabular-nums text-meta font-semibold', isActive ? 'text-primary' : 'text-muted-foreground')}>
                        {uids.length}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-44 p-2" side="top" align="start" sideOffset={4} onClick={(e) => e.stopPropagation()}>
                    <div className="space-y-0.5">
                      {uids.map(uid => {
                        const user = My.getUserFromRooms(uid);
                        const name = user?.name ?? uid.slice(0, 8);
                        const photo = user?.photoUrl ?? null;
                        const isMe = uid === My.user?.id;
                        return (
                          <div key={uid} className="flex items-center gap-2 px-1 py-0.5 rounded-md">
                            <UserAvatar src={photo} name={name} className="size-4 shrink-0" />
                            <span className="text-meta-sm font-medium text-foreground truncate flex-1">{name}</span>
                            {isMe && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleCommentReaction(type); }}
                                className="text-meta-sm text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                              >
                                <Trans>Remove</Trans>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}

            {/* QnA 채택 */}
            {isQna && isPostAuthor && comment.author.uid !== My.user?.id && (
              isAccepted ? (
                <button
                  onClick={onUnresolve}
                  disabled={!!acceptingId}
                  className="inline-flex items-center gap-1 text-meta-sm font-medium text-badge-success hover:text-badge-success/80 transition-colors cursor-pointer"
                >
                  <CheckCircle2 className="size-3" />
                  <Trans>Unmark as answer</Trans>
                </button>
              ) : !acceptedCommentId ? (
                <button
                  onClick={() => onAccept(comment.id)}
                  disabled={!!acceptingId}
                  className="inline-flex items-center gap-1 text-meta-sm font-medium text-muted-foreground hover:text-emerald-600 transition-colors cursor-pointer"
                >
                  <CheckCircle2 className="size-3" />
                  <Trans>Mark as answer</Trans>
                </button>
              ) : null
            )}

          </div>

          {/* 확인 현황 (Tasks 전용 + 멘션 있는 댓글만)
              ≤ 5명: peach bg row 안에 각 사용자 pill 펼침
              > 5명: 카운트 요약 + Popover (리액션 표시 패턴) */}
          {showReadReceipts && comment.mentioned_user_ids && comment.mentioned_user_ids.length > 0 && (() => {
            const mentionedIds = comment.mentioned_user_ids!;
            const receipts = receiptsByCommentId?.[comment.id] ?? [];
            const readMap = Object.fromEntries(receipts.map(r => [r.user_id, r.read_at]));
            const readSet = new Set(receipts.map(r => r.user_id));
            const getName = (uid: string) => {
              const member = mentionMembers?.find(m => m.id === uid);
              return member?.name ?? uid.slice(0, 8);
            };
            const confirmedCount = mentionedIds.filter(id => readSet.has(id)).length;
            const totalCount = mentionedIds.length;
            const allConfirmed = confirmedCount === totalCount;

            const Pill = ({ uid }: { uid: string }) => {
              const hasRead = readSet.has(uid);
              const name = getName(uid);
              const readAt = readMap[uid];
              return (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-[7px] py-px rounded-full text-meta-sm font-medium border',
                    hasRead
                      ? 'text-badge-success border-[color-mix(in_srgb,rgb(var(--badge-success))_35%,rgb(var(--border)))] bg-badge-success-bg'
                      : 'text-muted-foreground bg-card border-border',
                  )}
                  title={hasRead && readAt
                    ? t`${name} confirmed (${new Date(readAt).toLocaleString()})`
                    : t`${name} not confirmed`}
                >
                  {hasRead
                    ? <Check className="size-2.5" />
                    : <Hourglass className="size-2.5" />}
                  <span>{name}</span>
                </span>
              );
            };

            // ≤ 5명: inline 펼침
            if (totalCount <= 5) {
              return (
                <div className="mt-1.5 inline-flex items-center gap-1.5 flex-wrap">
                  {mentionedIds.map(uid => <Pill key={uid} uid={uid} />)}
                </div>
              );
            }

            // > 5명: 카운트 요약 + Popover
            return (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      'mt-1.5 inline-flex items-center gap-1 px-2 py-px rounded-full text-meta-sm font-medium border cursor-pointer transition-colors',
                      allConfirmed
                        ? 'text-badge-success border-[color-mix(in_srgb,rgb(var(--badge-success))_35%,rgb(var(--border)))] bg-badge-success-bg hover:brightness-95'
                        : 'text-muted-foreground bg-card border-border hover:border-[color-mix(in_srgb,rgb(var(--primary))_35%,rgb(var(--border)))]',
                    )}
                  >
                    {allConfirmed
                      ? <Check className="size-2.5" />
                      : <Hourglass className="size-2.5" />}
                    <span className="tabular-nums">
                      <span className={allConfirmed ? '' : 'text-primary'}>{confirmedCount}</span>
                      <span className="text-muted-foreground"> / {totalCount}</span>
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-2 w-auto max-w-[280px]" side="top" align="start" sideOffset={4}>
                  <div className="flex flex-wrap gap-1.5">
                    {mentionedIds.map(uid => <Pill key={uid} uid={uid} />)}
                  </div>
                </PopoverContent>
              </Popover>
            );
          })()}

        </div>
      </div>

      {/* 쓰레드 답글 목록 — 채팅 쓰레드처럼 시간순 flat */}
      {showReplies && replies.length > 0 && (
        <div className="ml-9 mt-2 space-y-2 border-l-2 border-border/40 pl-3">
          {replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              isQna={isQna}
              isPostAuthor={isPostAuthor}
              acceptedCommentId={acceptedCommentId}
              acceptingId={acceptingId}
              onAccept={onAccept}
              onUnresolve={onUnresolve}
              onDelete={onDelete}
              isReply
              mentionMembers={mentionMembers}
              showReadReceipts={showReadReceipts}
              receiptsByCommentId={receiptsByCommentId}
              threadSiblings={replies}
              onReplyClick={onReplyClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── CommentSection ──────────────────────────────────────────

const CommentSection: React.FC<Props> = ({ postId, comments, onCommentAdded, isQna, post, onPostUpdated, canComment, mentionMembers, showReadReceipts, channelId, canMentionAll }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [receiptsByCommentId, setReceiptsByCommentId] = useState<Record<string, { user_id: string; read_at: string }[]>>({});
  // 채팅 쓰레드식 답글 모드 — 메인 입력창이 답글 모드로 전환됨
  const [replyingTo, setReplyingTo] = useState<CommentModel | null>(null);
  // 쓰레드 루트 댓글 id → bump 카운터. 변경되면 해당 CommentItem이 replies를 재로드 + 펼침.
  const [refreshKeyByThread, setRefreshKeyByThread] = useState<Record<string, number>>({});
  // 입력 영역 ref — 답글 모드 진입 시 스크롤 + 포커스
  const inputAreaRef = useRef<HTMLDivElement>(null);
  // react-mentions suggestions — memoize 해서 매 렌더마다 새 배열 생성으로 인한 내부 상태 리셋 방지
  const mentionSuggestions = useMemo<SuggestionDataItem[]>(() => {
    const base = (mentionMembers ?? []).map(m => ({ id: m.id, display: m.name }));
    // @전체(전원 멘션) — 권한 있을 때 최상단. id='everyone' sentinel
    return canMentionAll ? [{ id: 'everyone', display: t`Everyone` }, ...base] : base;
  }, [mentionMembers, canMentionAll]);
  // 첨부파일 (전송 대기 중) + 업로드 진행
  const [pendingAttachments, setPendingAttachments] = useState<CommentAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showChatFilePicker, setShowChatFilePicker] = useState(false);

  /** 로컬 파일 선택 → 업로드 → pending 추가 */
  const handleLocalFileSelect = useCallback(async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0 || !My.user?.id) return;
    setUploading(true);
    try {
      const arr = Array.from(filesList);
      const uploaded: CommentAttachment[] = [];
      for (const f of arr) {
        const result = await uploadCommentAttachment(f, My.user.id, postId);
        uploaded.push({ ...result, source: 'local' });
      }
      setPendingAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('[CommentSection] 파일 업로드 실패:', err);
      toast({ title: t`Failed to upload file`, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [postId]);

  const removePendingAttachment = (index: number) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  /** 채팅 파일 피커에서 선택된 파일을 pending에 추가 */
  const handleChatFilePicked = (att: CommentAttachment) => {
    setPendingAttachments(prev => [...prev, att]);
    setShowChatFilePicker(false);
  };

  const acceptedCommentId = post?.metadata?.accepted_comment_id as string | undefined;
  const isPostAuthor = My.user?.id === post?.author.uid;

  // 멘션 있는 댓글 ID 목록 — 메모이즈로 effect 의존성 안정화
  const mentionedCommentIds = useMemo(
    () => comments.filter(c => c.mentioned_user_ids && c.mentioned_user_ids.length > 0).map(c => c.id),
    [comments],
  );
  const mentionedCommentIdsKey = mentionedCommentIds.join(',');

  // 확인 현황 데이터 로드 + 본인 멘션된 댓글 자동 read 기록
  useEffect(() => {
    if (!showReadReceipts) return;
    if (mentionedCommentIds.length === 0) {
      setReceiptsByCommentId({});
      return;
    }
    const uid = My.user?.id;
    if (uid) {
      // 본인이 멘션된 댓글들 read 기록 (순차 호출, 실패는 무시)
      for (const c of comments) {
        if (c.mentioned_user_ids?.includes(uid)) {
          recordCommentRead(c.id, uid);
        }
      }
    }
    getCommentReadReceiptsBatch(mentionedCommentIds).then(setReceiptsByCommentId);
    // mentionedCommentIdsKey 변화 시에만 재실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReadReceipts, mentionedCommentIdsKey]);

  // 채택된 댓글을 최상단에 표시 + 최상위 댓글만 (대댓글은 부모 아래에 토글 렌더)
  const topLevelComments = useMemo(() => {
    const tops = comments.filter(c => !c.parent_comment_id);
    if (!isQna || !acceptedCommentId) return tops;
    const accepted = tops.filter(c => c.id === acceptedCommentId);
    const others = tops.filter(c => c.id !== acceptedCommentId);
    return [...accepted, ...others];
  }, [comments, isQna, acceptedCommentId]);

  /** 답글 버튼 클릭 — 메인 입력창을 답글 모드로 전환, @작성자 prefill, 스크롤 + 포커스 */
  const handleReplyClick = useCallback((target: CommentModel) => {
    setReplyingTo(target);
    const targetIsOther = target.author.uid !== My.user?.id;
    setText(targetIsOther ? `@${target.author.name} ` : '');
    // 입력창으로 스크롤 + 포커스
    setTimeout(() => {
      inputAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      const ta = inputAreaRef.current?.querySelector('textarea');
      ta?.focus();
      // 커서를 끝으로
      if (ta) {
        const len = ta.value.length;
        ta.setSelectionRange(len, len);
      }
    }, 50);
  }, []);

  const clearReplyingTo = useCallback(() => {
    setReplyingTo(null);
    setText('');
  }, []);

  const handleSubmit = async () => {
    // 첨부만 있고 텍스트 없는 경우도 허용
    if ((!text.trim() && pendingAttachments.length === 0) || submitting || !My.user) return;
    setSubmitting(true);
    try {
      const author: PostAuthor = {
        uid: My.user.id,
        name: My.user.name,
        photoUrl: My.user.photoUrl ?? null,
      };
      const mentionedIdsRaw = extractMentionIdsFromMarkup(text);
      const mentionAll = mentionedIdsRaw.includes('everyone');   // @전체 감지
      const mentionedIds = mentionedIdsRaw.filter(id => id !== 'everyone');
      const attachments = pendingAttachments.length > 0 ? pendingAttachments : undefined;
      if (replyingTo) {
        // 답글 모드: 쓰레드 루트 계산 + 인용 대상 기록 (채팅의 parentMessageId / replyToMessageId 패턴)
        const threadRootId = replyingTo.parent_comment_id ?? replyingTo.id;
        const replyToCommentId = replyingTo.parent_comment_id ? replyingTo.id : undefined;
        await createComment({
          target_id: postId,
          target_type: 'post',
          text: text.trim(),
          author,
          parent_comment_id: threadRootId,
          reply_to_comment_id: replyToCommentId,
          mentioned_user_ids: mentionedIds.length > 0 ? mentionedIds : undefined,
          attachments,
        });
        // 해당 쓰레드의 top-level CommentItem이 replies를 재로드하도록 트리거 bump
        setRefreshKeyByThread(prev => ({
          ...prev,
          [threadRootId]: (prev[threadRootId] ?? 0) + 1,
        }));
        setReplyingTo(null);
      } else {
        // 최상위 댓글
        await createComment({
          target_id: postId,
          target_type: 'post',
          text: text.trim(),
          author,
          mentioned_user_ids: mentionedIds.length > 0 ? mentionedIds : undefined,
          attachments,
        });
      }
      // 댓글 알림 — notify_comment 켠 멤버 + 멘션받은 사람 (functions board-notify 가 필터)
      if (post) {
        notifyNewComment(
          post.workspace_id,
          post.board_id,
          post.title,
          post.title,
          postId,
          text.trim(),
          mentionedIds,
          mentionAll,
        );
      }
      setText('');
      setPendingAttachments([]);
      onCommentAdded();
    } catch (err) {
      console.error('[CommentSection] 댓글 작성 실패:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      onCommentAdded();
    } catch (err) {
      console.error('[CommentSection] 댓글 삭제 실패:', err);
    }
  };

  const handleAccept = async (commentId: string) => {
    if (acceptingId) return;
    setAcceptingId(commentId);
    try {
      const updated = await acceptAnswer(postId, commentId);
      onPostUpdated?.(updated);
      toast({ title: t`Answer marked as accepted` });
    } catch (err) {
      console.error('[CommentSection] 채택 실패:', err);
      toast({ title: t`Failed to mark as answer`, variant: 'destructive' });
    } finally {
      setAcceptingId(null);
    }
  };

  const handleUnresolve = async () => {
    if (acceptingId) return;
    setAcceptingId('unresolve');
    try {
      const updated = await unresolveQuestion(postId);
      onPostUpdated?.(updated);
      toast({ title: t`Answer acceptance canceled` });
    } catch (err) {
      console.error('[CommentSection] 채택 취소 실패:', err);
    } finally {
      setAcceptingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 채팅 입력 패턴: Enter는 줄바꿈, Cmd/Ctrl+Enter가 전송
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !(e.nativeEvent as any).isComposing) {
      e.preventDefault();
      handleSubmit();
      return;
    }
    if (e.key === 'Escape' && replyingTo) {
      e.preventDefault();
      clearReplyingTo();
    }
  };

  return (
    <div className="border-t border-border pt-4 pb-5 mt-2 sm:pt-5 sm:mt-4">
      <h3 className="text-body font-bold text-[color:rgb(var(--text-title))] mb-4">
        {isQna ? <Trans>Answers</Trans> : <Trans>Comments</Trans>} · {comments.length}
      </h3>

      {/* 댓글 목록 — prototype `.ih-comment { border-bottom: 1px solid var(--border) }` */}
      <div className="divide-y divide-border">
        {topLevelComments.map(comment => (
          <div key={comment.id} className="py-3 first:pt-0 sm:py-3.5 sm:first:pt-0">
            <CommentItem
              comment={comment}
              isQna={isQna}
              isPostAuthor={isPostAuthor}
              acceptedCommentId={acceptedCommentId}
              acceptingId={acceptingId}
              onAccept={handleAccept}
              onUnresolve={handleUnresolve}
              onDelete={handleDelete}
              mentionMembers={mentionMembers}
              showReadReceipts={showReadReceipts}
              receiptsByCommentId={receiptsByCommentId}
              onReplyClick={handleReplyClick}
              refreshRepliesTrigger={refreshKeyByThread[comment.id] ?? 0}
            />
          </div>
        ))}
      </div>

      {comments.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-6">
          {isQna ? <Trans>No answers yet. Be the first to answer.</Trans> : <Trans>No comments yet. Be the first to comment.</Trans>}
        </p>
      )}

      {/* 댓글 입력 영역 */}
      {canComment !== false ? (
        <div ref={inputAreaRef} className="border-t border-border pt-4 mt-2 space-y-2 sm:pt-5 sm:mt-3">
          {/* 첨부 칩 — 전송 대기 중인 첨부파일 미리보기 */}
          {(pendingAttachments.length > 0 || uploading) && (
            <div className="flex flex-wrap gap-2">
              {pendingAttachments.map((att, idx) => (
                <div
                  key={`${att.url}-${idx}`}
                  className="group inline-flex items-center gap-2 bg-muted/60 rounded-lg px-2 py-1.5 text-xs max-w-[260px]"
                >
                  {att.group === 'image' ? (
                    <img src={att.url} alt={att.filename} className="size-8 rounded object-cover shrink-0" />
                  ) : (
                    <FileText className="size-4 text-muted-foreground shrink-0" />
                  )}
                  <span className="truncate flex-1 min-w-0" title={att.filename}>{att.filename}</span>
                  {att.source === 'chat' && (
                    <span className="text-meta-sm text-muted-foreground/70 shrink-0"><Trans>Chat</Trans></span>
                  )}
                  <button
                    onClick={() => removePendingAttachment(idx)}
                    className="text-muted-foreground/60 hover:text-destructive transition-colors shrink-0"
                    title={t`Remove`}
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
              {uploading && (
                <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1.5">
                  <Loader2 className="size-3.5 animate-spin" />
                  <Trans>Uploading...</Trans>
                </div>
              )}
            </div>
          )}

          {/* 답글 모드 인용 배너 — composer pill 과 동일한 가로 폭 (외곽 spacer 없음) */}
          {replyingTo && (
            <div className="w-full bg-msg-selected-bg flex gap-2 items-start p-2 rounded-lg">
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <p className="font-semibold text-label text-muted-foreground leading-tight m-0">
                  <Trans>Reply to {replyingTo.author.name}</Trans>
                </p>
                <p
                  className="font-normal text-label text-foreground/80 leading-snug truncate m-0"
                  dangerouslySetInnerHTML={{
                    __html: renderCommentText(
                      replyingTo.text,
                      replyingTo.mentioned_user_ids,
                      mentionMembers,
                      My.user?.id,
                    ),
                  }}
                />
              </div>
              <button
                onClick={clearReplyingTo}
                className="flex items-center justify-center rounded-md size-6 shrink-0 text-muted-foreground hover:bg-primary/15 cursor-pointer"
                title={t`Cancel reply`}
              >
                <X className="size-3.5" />
              </button>
            </div>
          )}

          {/* hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={e => handleLocalFileSelect(e.target.files)}
          />

          {/* Composer — prototype `.ih-comment-composer` pill: + / textarea / send 한 컨테이너 */}
          <div
            className="flex items-end gap-2 bg-muted rounded-3xl pl-2 pr-1.5 py-1.5 cursor-text transition-colors focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/30"
            onClick={() => {
              const ta = inputAreaRef.current?.querySelector('textarea');
              ta?.focus();
            }}
          >
            {/* + 첨부 */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  disabled={uploading}
                  onClick={(e) => e.stopPropagation()}
                  className="size-[34px] shrink-0 self-end rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors disabled:opacity-50"
                  title={t`Attach`}
                >
                  <Plus className="size-[18px]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1" side="top" align="start" sideOffset={4}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors cursor-pointer text-left"
                >
                  <Paperclip className="size-4 text-muted-foreground" />
                  <Trans>Upload file</Trans>
                </button>
                {channelId && (
                  <button
                    onClick={() => setShowChatFilePicker(true)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted transition-colors cursor-pointer text-left"
                  >
                    <MessageSquareQuote className="size-4 text-muted-foreground" />
                    <Trans>From chat</Trans>
                  </button>
                )}
              </PopoverContent>
            </Popover>

            {/* 입력창 — auto-grow textarea / MentionTextArea (동작 보존) */}
            <div className="flex-1 min-w-0 py-1.5">
              {mentionMembers && mentionMembers.length > 0 ? (
                <MentionTextArea
                  text={text}
                  onChange={setText}
                  onSubmit={() => handleSubmit()}
                  placeholder={
                    replyingTo
                      ? t`Reply to ${replyingTo.author.name}... (Cmd+Enter to send)`
                      : (isQna ? t`Enter an answer... (Cmd+Enter to send)` : t`Add a comment... (Cmd+Enter to send)`)
                  }
                  suggestions={mentionSuggestions}
                  className="flex-1"
                />
              ) : (
                <textarea
                  value={text}
                  onChange={e => {
                    setText(e.target.value);
                    // 일반 textarea도 auto-grow
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 210) + 'px';
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    replyingTo
                      ? t`Reply to ${replyingTo.author.name}... (Cmd+Enter to send)`
                      : (isQna ? t`Enter an answer... (Cmd+Enter to send)` : t`Add a comment... (Cmd+Enter to send)`)
                  }
                  rows={1}
                  className="w-full min-w-0 bg-transparent border-0 outline-none focus:ring-0 p-0 m-0 resize-none text-body placeholder:text-muted-foreground leading-[20px] overflow-auto"
                />
              )}
            </div>

            {/* Send */}
            <Button
              size="icon"
              onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
              disabled={(!text.trim() && pendingAttachments.length === 0) || submitting || uploading}
              className="size-[34px] shrink-0 self-end rounded-full"
              title={t`Send (Cmd+Enter)`}
            >
              <Send className="size-4" />
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-4 border-t border-border mt-2">
          <Trans>You don't have permission to comment.</Trans>
        </p>
      )}

      {/* 채팅 파일 피커 — channelId 있을 때만 */}
      {channelId && (
        <ChatFilePickerModal
          open={showChatFilePicker}
          channelId={channelId}
          onClose={() => setShowChatFilePicker(false)}
          onPick={handleChatFilePicked}
        />
      )}
    </div>
  );
};

export default CommentSection;
